import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  CheckCircle2, 
  Clock, 
  Circle,
  AlertTriangle,
  RefreshCw,
  Calendar,
  Users,
  Target,
  ChevronRight,
  ChevronDown,
  Flag,
  Milestone,
  ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectTrackerTab({ userRole = "executive" }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [expandedWorkstreams, setExpandedWorkstreams] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem("executiveToken") || 
                  localStorage.getItem("chroToken") || 
                  localStorage.getItem("projectManagerToken") ||
                  localStorage.getItem("authToken");
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, timelineRes] = await Promise.all([
        fetch(`${API}/project/tasks`, getAuthHeaders()),
        fetch(`${API}/project/timeline`, getAuthHeaders())
      ]);
      
      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        setData(tasksData);
        // Auto-expand current phase
        if (tasksData.summary?.currentPhase) {
          setExpandedWorkstreams({ [tasksData.summary.currentPhase]: true });
        }
      }
      
      if (timelineRes.ok) {
        const timeline = await timelineRes.json();
        setTimelineData(timeline);
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleWorkstream = (ws) => {
    setExpandedWorkstreams(prev => ({ ...prev, [ws]: !prev[ws] }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Complete':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'In Progress':
        return <Clock className="w-5 h-5 text-blue-500 animate-pulse" />;
      default:
        return <Circle className="w-5 h-5 text-slate-300" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Complete':
        return { bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-500' };
      case 'In Progress':
        return { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500' };
      default:
        return { bg: 'bg-slate-300', light: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300' };
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Critical': 'bg-red-100 text-red-700 border-red-300',
      'High': 'bg-orange-100 text-orange-700 border-orange-300',
      'Medium': 'bg-blue-100 text-blue-700 border-blue-300',
      'Low': 'bg-slate-100 text-slate-600 border-slate-300'
    };
    return colors[priority] || colors['Medium'];
  };

  const getRiskBadge = (risk) => {
    const colors = {
      'High': 'bg-red-500 text-white',
      'Medium': 'bg-amber-500 text-white',
      'Low': 'bg-emerald-500 text-white'
    };
    return colors[risk] || colors['Low'];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-[#63C465]" />
      </div>
    );
  }

  const { summary, workstreams, workstreamOrder, atRiskTasks } = data;

  return (
    <div className="space-y-6 p-6 bg-gradient-to-br from-slate-50 to-white min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#00AEEF]">Project Timeline</h2>
          <p className="text-sm text-muted-foreground">Sagicor Technology Assessment Initiative</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-[#00AEEF] to-[#2a4a4a] text-white col-span-2">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/70">Overall Progress</p>
                <p className="text-4xl font-bold">{summary.overallProgress}%</p>
              </div>
              <div className="w-20 h-20 relative">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle cx="40" cy="40" r="36" stroke="rgba(255,255,255,0.2)" strokeWidth="8" fill="none" />
                  <circle 
                    cx="40" cy="40" r="36" 
                    stroke="#63C465" 
                    strokeWidth="8" 
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 36 * summary.overallProgress / 100} ${2 * Math.PI * 36}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Target className="w-8 h-8 text-[#63C465]" />
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-xs text-white/70">Current Phase</p>
              <p className="font-semibold text-[#63C465]">{summary.currentPhase}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-emerald-600">{summary.completed}</p>
            <p className="text-xs text-muted-foreground">Complete</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 text-center">
            <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-blue-600">{summary.inProgress}</p>
            <p className="text-xs text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 text-center">
            <Circle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-slate-600">{summary.notStarted}</p>
            <p className="text-xs text-muted-foreground">Not Started</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 text-center">
            <Calendar className="w-8 h-8 text-[#63C465] mx-auto mb-2" />
            <p className="text-3xl font-bold text-[#00AEEF]">{summary.totalTasks}</p>
            <p className="text-xs text-muted-foreground">Total Tasks</p>
          </CardContent>
        </Card>
      </div>

      {/* Visual Timeline */}
      {timelineData && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Milestone className="w-5 h-5 text-[#63C465]" />
              Project Phases
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Timeline track */}
              <div className="absolute top-6 left-0 right-0 h-2 bg-slate-200 rounded-full" />
              <div 
                className="absolute top-6 left-0 h-2 bg-gradient-to-r from-emerald-500 to-[#63C465] rounded-full transition-all duration-1000"
                style={{ width: `${summary.overallProgress}%` }}
              />
              
              {/* Phase markers */}
              <div className="relative flex justify-between pt-0 pb-16">
                {timelineData.phases.slice(0, 8).map((phase, index) => {
                  const colors = getStatusColor(phase.status);
                  const isActive = phase.name === summary.currentPhase;
                  
                  return (
                    <TooltipProvider key={index}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col items-center cursor-pointer group">
                            <div className={`w-4 h-4 rounded-full ${colors.bg} ${isActive ? 'ring-4 ring-[#63C465]/30 scale-125' : ''} transition-all z-10`} />
                            <div className={`mt-4 text-center max-w-[100px] ${isActive ? 'font-bold' : ''}`}>
                              <p className="text-[10px] text-muted-foreground leading-tight">{phase.name.split(' - ')[0]}</p>
                              <p className={`text-xs font-semibold ${colors.text}`}>{phase.percentComplete}%</p>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div className="space-y-1">
                            <p className="font-semibold">{phase.name}</p>
                            <p className="text-xs">{phase.completedTasks}/{phase.taskCount} tasks complete</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(phase.startDate)} → {formatDate(phase.endDate)}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Milestones */}
      {timelineData?.milestones && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-amber-50 to-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <Flag className="w-5 h-5" />
              Key Milestones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {timelineData.milestones.map((milestone, i) => (
                <div 
                  key={i}
                  className={`flex-shrink-0 px-4 py-3 rounded-lg border-2 ${
                    milestone.status === 'Complete' 
                      ? 'bg-emerald-50 border-emerald-300' 
                      : milestone.status === 'In Progress'
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {getStatusIcon(milestone.status)}
                    <div>
                      <p className="text-sm font-semibold whitespace-nowrap">{milestone.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {milestone.completionDate ? `✓ ${formatDate(milestone.completionDate)}` : formatDate(milestone.date)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* At-Risk Tasks */}
      {atRiskTasks && atRiskTasks.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Tasks Requiring Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {atRiskTasks.map((task, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={getRiskBadge(task.riskLevel)}>{task.riskLevel}</Badge>
                    <div>
                      <p className="font-medium text-sm">{task.taskName}</p>
                      <p className="text-xs text-muted-foreground">{task.workstream}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{task.owner}</p>
                    <p className="text-xs text-muted-foreground">Due: {formatDate(task.targetDate)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Workstream View */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-[#63C465]" />
            Detailed Task Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              {workstreamOrder.filter(ws => workstreams[ws]).map((ws, wsIndex) => {
                const wsTasks = workstreams[ws] || [];
                const completed = wsTasks.filter(t => t.status === 'Complete').length;
                const progress = wsTasks.length > 0 ? (completed / wsTasks.length) * 100 : 0;
                const isExpanded = expandedWorkstreams[ws];
                const isCurrentPhase = ws === summary.currentPhase;
                
                return (
                  <div 
                    key={ws} 
                    className={`border rounded-lg overflow-hidden ${isCurrentPhase ? 'ring-2 ring-[#63C465]' : ''}`}
                  >
                    {/* Workstream Header */}
                    <div 
                      className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                        progress === 100 ? 'bg-emerald-50' : isCurrentPhase ? 'bg-blue-50' : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      onClick={() => toggleWorkstream(ws)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                          progress === 100 ? 'bg-emerald-500' : progress > 0 ? 'bg-blue-500' : 'bg-slate-400'
                        }`}>
                          {wsIndex + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold">{ws}</p>
                            {isCurrentPhase && (
                              <Badge className="bg-[#63C465] text-white text-xs">Current Phase</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-xs text-muted-foreground">{completed}/{wsTasks.length} tasks</span>
                            <Progress value={progress} className="w-24 h-2" />
                            <span className="text-xs font-semibold">{Math.round(progress)}%</span>
                          </div>
                        </div>
                      </div>
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </div>
                    
                    {/* Expanded Tasks */}
                    {isExpanded && (
                      <div className="border-t bg-white">
                        <table className="w-full">
                          <thead>
                            <tr className="text-xs text-muted-foreground border-b">
                              <th className="text-left p-3 w-8"></th>
                              <th className="text-left p-3">Task</th>
                              <th className="text-center p-3">Region</th>
                              <th className="text-center p-3">Owner</th>
                              <th className="text-center p-3">Priority</th>
                              <th className="text-center p-3">Target</th>
                              <th className="text-center p-3">Progress</th>
                            </tr>
                          </thead>
                          <tbody>
                            {wsTasks.map((task, i) => (
                              <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                                <td className="p-3">
                                  {getStatusIcon(task.status)}
                                </td>
                                <td className="p-3">
                                  <p className="font-medium text-sm">{task.taskName}</p>
                                  {task.notes && (
                                    <p className="text-xs text-muted-foreground mt-1 truncate max-w-xs">{task.notes}</p>
                                  )}
                                </td>
                                <td className="p-3 text-center">
                                  <Badge variant="outline" className="text-xs">{task.region || 'Global'}</Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <span className="text-sm">{task.owner}</span>
                                </td>
                                <td className="p-3 text-center">
                                  <Badge className={getPriorityBadge(task.priority)}>{task.priority}</Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <span className="text-sm">{formatDate(task.targetDate)}</span>
                                </td>
                                <td className="p-3 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <Progress value={task.percentComplete} className="w-16 h-2" />
                                    <span className="text-xs font-semibold w-10">{task.percentComplete}%</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
